import { useMemo } from "react"
import { Button, Popconfirm, Space, Table, Tag } from "antd"
import { DownloadOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import dayjs from "dayjs"
import { ID } from "@/types/common"
import { FileAsset, FileAssetTableProps } from "../types"
import { formatFileSize, getFileTypePresentation } from "../helpers"

const FileAssetTable = ({
  items,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  onDownload,
  pagination,
  onPageChange,
}: FileAssetTableProps) => {
  const columns: ColumnsType<FileAsset> = useMemo(
    () => [
      {
        title: "文件名称",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
      },
      {
        title: "原始文件名",
        dataIndex: "originalName",
        key: "originalName",
        ellipsis: true,
      },
      {
        title: "文件类型",
        dataIndex: "extension",
        key: "extension",
        width: 220,
        render: (_: string | null | undefined, record) => {
          const { icon: Icon, label, colorClassName, suffix } =
            getFileTypePresentation(record.extension)

          return (
            <div className="flex items-center gap-3">
              <div className={`rounded-md bg-slate-100 p-2 ${colorClassName}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-foreground">{label}</div>
                <div className="truncate text-xs text-gray-500">
                  {suffix}
                  {record.mimeType ? ` · ${record.mimeType}` : ""}
                </div>
              </div>
            </div>
          )
        },
      },
      {
        title: "文件大小",
        dataIndex: "size",
        key: "size",
        width: 120,
        render: (size: number) => formatFileSize(size),
      },
      {
        title: "绑定二维码",
        dataIndex: "boundQrCodeCount",
        key: "boundQrCodeCount",
        width: 120,
        render: (count: number) => (
          <Tag color={count > 0 ? "processing" : "default"}>{count}</Tag>
        ),
      },
      {
        title: "上传时间",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 180,
        render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm"),
      },
      {
        title: "操作",
        key: "action",
        width: 230,
        fixed: "right",
        render: (_, record) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => onDownload(record)}
            >
              下载
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确认删除"
              description={
                record.boundQrCodeCount > 0
                  ? `文件“${record.name}”已绑定二维码，删除会失败。是否继续尝试？`
                  : `确定要删除文件“${record.name}”吗？`
              }
              onConfirm={() => onDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [onDelete, onDownload, onEdit],
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
      }
    : undefined

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination?.page,
            pageSize: pagination?.pageSize,
            total: pagination?.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
              onPageChange?.(page, pageSize)
            },
          }}
        />
      </div>
    </div>
  )
}

export default FileAssetTable
